// Owner-editable editorial rules for generated articles, plus a deterministic
// post-check so a rule is enforced on the output instead of only suggested in
// the prompt. Pure TypeScript with no imports: the same file content runs in
// the Vite admin UI and (duplicated, see the note in the edge function) in the
// Deno edge runtime.
//
// Rules are free text written by the site owner. Lines are parsed into three
// buckets:
//   - literal banned phrases (quoted text on a prohibition line),
//   - named detectors (a contrast construction ban, heading variety),
//   - prompt-only lines the code cannot check (tone, voice).
// Anything in the first two buckets is verified after generation; the third is
// still sent to the model as part of the rules block.

export interface StyleViolation {
  rule: string;
  excerpt: string;
}

export type StyleDetector = "contrast_negation" | "repetitive_headings";

export interface ParsedStandingRules {
  bannedPhrases: string[];
  detectors: StyleDetector[];
  promptOnly: string[];
}

const MAX_CONTENT_CHARS = 200_000;
const MAX_RULES_CHARS = 3000;
const MAX_FEEDBACK_ITEMS = 8;
const EXCERPT_MAX = 160;

// "This isn't X, it's Y" and its variants. No unbounded quantifiers and no
// nested repetition, so these stay linear on large input.
const CONTRAST_PATTERNS: RegExp[] = [
  // same sentence: "<subject> isn't X, it's Y" (comma, semicolon, colon or dash)
  /\b\w+\s+(?:isn't|is not|aren't|are not|wasn't|was not)\b[^.!?\n]{1,80}?(?:,|;|:|\s[—–-]+\s?|—)\s*(?:it's|it is|this is|that's|that is|they're|they are|these are|those are|here's)\b/i,
  // across sentences: "X isn't Y. It's Z."
  /\b(?:isn't|is not|aren't|are not|wasn't|was not)\b[^.!?\n]{1,80}[.!?]\s+(?:it's|it is|this is|that's|that is|they're|they are|these are)\b/i,
  // "It's not X, it's Y" / "That's not X. That's Y."
  /\b(?:it's|that's|this is|it is|that is)\s+not\b[^.!?\n]{1,80}?(?:,|;|:|—|[.!?]\s+)\s*(?:it's|it is|that's|that is|this is|they're)\b/i,
  // "isn't just X, it's Y" / "isn't only X but Y"
  /\b(?:isn't|is not|aren't|are not)\s+(?:just|only|merely|simply)\b[^.!?\n]{1,80}?,?\s*(?:but|it's|it is|they're|they are)\b/i,
];

function normalize(text: string): string {
  return text.replace(/[‘’‚‛′]/g, "'").replace(/[“”„‟″]/g, '"');
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function trimExcerpt(s: string): string {
  const t = s.replace(/\s+/g, " ").trim();
  return t.length > EXCERPT_MAX ? `${t.slice(0, EXCERPT_MAX - 1)}…` : t;
}

// The sentence containing the match, so the rewrite feedback points at a
// passage the model can find, not just a fragment.
function sentenceAround(text: string, index: number, length: number): string {
  const before = text.slice(0, index);
  const cut = Math.max(
    before.lastIndexOf(". "),
    before.lastIndexOf("! "),
    before.lastIndexOf("? "),
  );
  const start = cut < 0 ? 0 : cut + 2;
  const rest = text.slice(index + length);
  const e = rest.search(/[.!?](?:\s|$)/);
  const end = e < 0 ? text.length : index + length + e + 1;
  return trimExcerpt(text.slice(start, end));
}

// Remove everything that is not running prose, so URLs, domains, emails and
// code never trigger a prose rule.
function stripNonProse(text: string): string {
  return text
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/`[^`\n]*`/g, " ")
    .replace(/\]\([^)\s]*\)/g, "]")
    .replace(/<[^>]+>/g, " ")
    .replace(/https?:\/\/\S+/g, " ")
    .replace(/\bwww\.\S+/g, " ")
    .replace(/\S+@\S+\.\S+/g, " ");
}

function isPlaceholderPattern(quoted: string): boolean {
  return /(?:^|[^A-Za-z])[XY](?:[^A-Za-z]|$)/.test(quoted);
}

// A line bans its quoted phrases only when a prohibition word governs
// using/saying/writing them. "Never forget to mention "x"" or "Do not skip the
// "x" section" are requirements, not bans.
function isPhraseBanLine(line: string): boolean {
  if (/\b(?:forget|skip|omit|leave out|miss|neglect)\b/i.test(line))
    return false;
  if (/^\s*(?:no|ban|banned)\b/i.test(line)) return true;
  return /\b(?:never|don't|do not|avoid|stop|ban|banned)\b[^"`]{0,50}\b(?:use|using|say|saying|write|writing|include|including|mention|mentioning|start|starting|open|opening|begin|beginning|phrase|phrases|word|words|term|terms|wording|language|expression|expressions)\b/i.test(
    line,
  );
}

export function parseStandingRules(
  rulesText: string | null | undefined,
): ParsedStandingRules {
  const out: ParsedStandingRules = {
    bannedPhrases: [],
    detectors: [],
    promptOnly: [],
  };
  if (!rulesText || !rulesText.trim()) return out;

  const lines = normalize(rulesText)
    .split("\n")
    .map((l) => l.replace(/^\s*(?:\d+[.)]|[-*•])\s*/, "").trim())
    .filter(Boolean);

  const addDetector = (d: StyleDetector) => {
    if (!out.detectors.includes(d)) out.detectors.push(d);
  };

  for (const line of lines) {
    let enforced = false;
    const prohibition = isPhraseBanLine(line);
    const quoted = Array.from(line.matchAll(/"([^"]+)"|`([^`]+)`/g))
      .map((m) => (m[1] ?? m[2] ?? "").trim())
      .filter(Boolean);

    const mentionsNegation =
      /isn'?t|is not|aren'?t|it'?s not|not just|not only/i.test(line);
    if (
      mentionsNegation &&
      (quoted.some(isPlaceholderPattern) ||
        /\b(?:construction|contrast)\b/i.test(line))
    ) {
      addDetector("contrast_negation");
      enforced = true;
    }

    if (
      /repetitive|consecutive headings|vary (?:the )?(?:headings|structure|sentence)|\bx to y\b/i.test(
        line,
      )
    ) {
      addDetector("repetitive_headings");
      enforced = true;
    }

    if (prohibition) {
      for (const q of quoted) {
        if (isPlaceholderPattern(q)) continue;
        if (!out.bannedPhrases.some((p) => p.toLowerCase() === q.toLowerCase()))
          out.bannedPhrases.push(q);
        enforced = true;
      }
    }

    if (!enforced) out.promptOnly.push(line);
  }
  return out;
}

interface HeadingKey {
  text: string;
  first: string;
  xToY: boolean;
  gerund: boolean;
}

function headingKeys(text: string): HeadingKey[] {
  const found: HeadingKey[] = [];
  let inFaq = false;
  for (const line of text.split("\n")) {
    const m = line.match(/^#{2,3}\s+(.+)$/);
    if (!m) continue;
    // FAQ questions legitimately start alike (How..., What...), so the
    // questions under a FAQ heading are not part of the variety check.
    if (/^##\s/.test(line)) inFaq = /\b(?:faq|frequently asked)\b/i.test(m[1]);
    else if (inFaq) continue;
    const words = m[1].trim().split(/\s+/);
    found.push({
      text: m[1].trim(),
      first: words[0].toLowerCase(),
      xToY: words.length >= 3 && words[1].toLowerCase() === "to",
      // "Patching...", "Replacing...", "Fixing...": a run of -ing openers reads
      // as a template even though no single word repeats.
      gerund: /^[a-z]{3,}ing$/.test(words[0].toLowerCase()),
    });
  }
  return found;
}

function repetitiveHeadings(text: string): StyleViolation | null {
  const h = headingKeys(text);
  for (let i = 0; i + 2 < h.length; i++) {
    const sameFirst =
      h[i].first === h[i + 1].first && h[i + 1].first === h[i + 2].first;
    const sameShape = h[i].xToY && h[i + 1].xToY && h[i + 2].xToY;
    const gerundRun = h[i].gerund && h[i + 1].gerund && h[i + 2].gerund;
    if (sameFirst || sameShape || gerundRun) {
      return {
        rule: "repetitive_headings",
        excerpt: trimExcerpt(
          `${h[i].text} / ${h[i + 1].text} / ${h[i + 2].text}`,
        ),
      };
    }
  }
  return null;
}

// The site publishes in English. A run of non-Latin script, or a sentence made
// of another language's function words, means the model drifted languages.
// eslint-disable-next-line no-misleading-character-class -- script ranges, matched as plain code points
const NON_LATIN_RUN = /[Ͱ-ϿЀ-ӿ֐-׿؀-ۿऀ-ॿ฀-๿぀-ヿ一-鿿가-힯]/;

// Letters used by Czech, Slovak, Polish, Turkish, Hungarian, Romanian and Croatian
// that US English never needs (Spanish and French accents are caught by word sets).
const NON_ENGLISH_LETTERS =
  /[ěščřžůťďňľĺŕąćęłńśźżğşőűăîșțđĚŠČŘŽŮŤĎŇĽĹŔĄĆĘŁŃŚŹŻĞŞŐŰĂÎȘȚĐ]/;

// Broken-encoding artifacts such as "â€™", "Ã©" and the U+FFFD replacement character.
const MOJIBAKE = /Ã[\u0080-¿]|â€[™œ\u009D\u0098”“˜¦]|Â[ -¿]|�/;

const ENGLISH_WORDS = new Set(
  "the and of to in is that for with are you your it on as be this have or not can will".split(
    " ",
  ),
);

const OTHER_LANGUAGE_WORDS: Record<string, Set<string>> = {
  spanish: new Set(
    "los las del que una por con para como pero más muy está son sus también donde cuando".split(
      " ",
    ),
  ),
  french: new Set(
    "les des est une pour avec dans qui nous vous sont être mais cette aussi très".split(
      " ",
    ),
  ),
  german: new Set(
    "der das und ist nicht mit eine für auf von dem sich auch oder wird sind".split(
      " ",
    ),
  ),
  portuguese: new Set(
    "não uma para você são mais dos das também está muito".split(" "),
  ),
  italian: new Set("il che della più anche questo molto sono".split(" ")),
};

// Reports up to five offending passages so a rewrite pass can fix them all,
// not just the first.
function languageDrift(prose: string): StyleViolation[] {
  const found: StyleViolation[] = [];
  const script = prose.match(NON_LATIN_RUN);
  if (script && script.index !== undefined) {
    found.push({
      rule: "language_drift",
      excerpt: sentenceAround(prose, script.index, script[0].length),
    });
  }
  const letters = prose.match(NON_ENGLISH_LETTERS);
  if (letters && letters.index !== undefined) {
    found.push({
      rule: "language_drift",
      excerpt: sentenceAround(prose, letters.index, 1),
    });
  }
  const garbled = prose.match(MOJIBAKE);
  if (garbled && garbled.index !== undefined) {
    found.push({
      rule: "garbled_text",
      excerpt: sentenceAround(prose, garbled.index, garbled[0].length),
    });
  }
  for (const line of prose.split("\n")) {
    for (const sentence of line.split(/(?<=[.!?])\s+/)) {
      const words = sentence.split(/\s+/);
      if (words.length < 8) continue;
      // Capitalised words are skipped so place names (Los Angeles, Des Moines)
      // never count as another language.
      const lower = words
        .map((w) => w.replace(/^[^\p{L}]+|[^\p{L}]+$/gu, ""))
        .filter((w) => w && w === w.toLowerCase());
      const english = lower.filter((w) => ENGLISH_WORDS.has(w)).length;
      for (const set of Object.values(OTHER_LANGUAGE_WORDS)) {
        const hits = lower.filter((w) => set.has(w)).length;
        if (hits >= 3 && hits > english) {
          found.push({
            rule: "language_drift",
            excerpt: sentence.slice(0, 200),
          });
          break;
        }
      }
      if (found.length >= 5) return found;
    }
  }
  return found;
}

// Plain-text conversion. Fields that are shown as plain text or placed in
// schema markup (how-to step text, excerpts, takeaways, meta descriptions)
// must never carry markdown syntax; this one converter serves every surface.
const HTML_ENTITIES: Record<string, string> = {
  "&amp;": "&",
  "&nbsp;": " ",
  "&quot;": '"',
  "&apos;": "'",
  "&#39;": "'",
  "&lt;": "<",
  "&gt;": ">",
  "&rsquo;": "’",
  "&lsquo;": "‘",
  "&ndash;": "–",
  "&mdash;": "—",
};

export function toPlainText(input: string | null | undefined): string {
  if (!input) return "";
  let out = String(input).replace(/\r\n?/g, "\n");
  out = out.replace(/[​-‍⁠﻿]/g, "");
  out = out.replace(/```[\s\S]*?(?:```|$)/g, " ");
  out = out.replace(/<!--[\s\S]*?-->/g, " ");
  out = out.replace(/<\/?[A-Za-z][^>\n]*>/g, " ");
  out = out.replace(/::[A-Za-z]+CTA::/g, " ");
  // Images carry no sentence value in a text field: drop them entirely.
  out = out.replace(/!\[[^\]]*\]\([^)]*\)/g, " ");
  out = out.replace(/\[([^\]]+)\]\([^)]*\)/g, "$1");
  out = out.replace(/\[([^\]]+)\]\[[^\]]*\]/g, "$1");
  out = out.replace(/^[ \t]*\[[^\]]+\]:[ \t]*\S.*$/gm, " ");
  out = out.replace(/\[\^[^\]]+\]/g, "");
  out = out.replace(
    /^[ \t]*\|?[ \t]*:?-{2,}:?[ \t]*(?:\|[ \t]*:?-{2,}:?[ \t]*)*\|?[ \t]*$/gm,
    " ",
  );
  out = out.replace(/^[ \t]*\|(.*)\|[ \t]*$/gm, (_m, row: string) =>
    row
      .split("|")
      .map((c) => c.trim())
      .filter(Boolean)
      .join(", "),
  );
  out = out.replace(/^[ \t]{0,3}#{1,6}[ \t]+/gm, "");
  out = out.replace(/^[ \t]{0,3}>[ \t]?/gm, "");
  out = out.replace(/^[ \t]*(?:[-*+•]|\d+[.)])[ \t]+/gm, "");
  out = out.replace(/^[ \t]*(?:-{3,}|\*{3,}|_{3,})[ \t]*$/gm, " ");
  out = out.replace(/(\*{1,3})(?=[^*\s])([^*\n]*?[^*\s])\1/g, "$2");
  out = out.replace(
    /(^|[^\w])(_{1,3})(?=[^_\s])([^_\n]*?[^_\s])\2(?=$|[^\w])/g,
    "$1$3",
  );
  out = out.replace(/~~([^~\n]+)~~/g, "$1");
  out = out.replace(/`([^`\n]+)`/g, "$1");
  out = out.replace(/\\([\\`*_{}[\]()#+\-.!|>~])/g, "$1");
  out = out.replace(/\*{2,}|_{2,}|~{2,}/g, "");
  out = out
    .replace(/(^|\s)[*_](?=\S)/g, "$1")
    .replace(/(?<=\S)[*_](?=\s|$)/g, "");
  out = out.replace(
    /&(?:amp|nbsp|quot|apos|lt|gt|rsquo|lsquo|ndash|mdash);|&#39;/g,
    (m) => HTML_ENTITIES[m] ?? m,
  );
  return out.replace(/\s+/g, " ").trim();
}

// Models sometimes place the space on the wrong side of a bold marker
// ("**Pay it off sooner. **The more" or "In a** fixed rate**,"), which markdown
// then prints as literal asterisks. Markers are paired left to right the way
// markdown pairs them (an opener needs a non-space after it, a closer a
// non-space before it); only markers that stay unpaired are repaired, so valid
// bold spans are never touched.
function repairEmphasisLine(line: string): string {
  if (!line.includes("**")) return line;
  type Marker = { idx: number; open: boolean; close: boolean };
  const markers: Marker[] = [];
  const re = /(?<!\*)\*\*(?!\*)/g;
  for (let m = re.exec(line); m; m = re.exec(line)) {
    const prev = line[m.index - 1] ?? " ";
    const next = line[m.index + 2] ?? " ";
    markers.push({
      idx: m.index,
      open: !/\s/.test(next),
      close: !/\s/.test(prev),
    });
  }
  const stack: Marker[] = [];
  const paired = new Set<Marker>();
  for (const mk of markers) {
    if (mk.close && stack.length) {
      paired.add(stack.pop() as Marker);
      paired.add(mk);
    } else if (mk.open) {
      stack.push(mk);
    }
  }
  const loose = markers.filter((mk) => !paired.has(mk));
  const edits: Array<{ start: number; end: number; text: string }> = [];
  for (let i = 0; i + 1 < loose.length; i++) {
    const a = loose[i];
    const b = loose[i + 1];
    const between = line.slice(a.idx + 2, b.idx);
    if (!between || between.length > 240) continue;
    if (a.open && !a.close && b.open && !b.close && /[ \t]$/.test(between)) {
      // "**text. **Next": the closing marker carries the space that belongs after it.
      const spaces = between.length - between.trimEnd().length;
      edits.push({ start: b.idx - spaces, end: b.idx + 2, text: "** " });
      i++;
    } else if (a.close && !a.open && b.close && /^[ \t]/.test(between)) {
      // "a** text**": the opening marker carries the space that belongs before it.
      const spaces = between.length - between.trimStart().length;
      edits.push({ start: a.idx, end: a.idx + 2 + spaces, text: " **" });
      i++;
    } else if (
      a.close &&
      !a.open &&
      b.open &&
      !b.close &&
      /^[ \t]+$/.test(between)
    ) {
      // "word** **word": an empty bold span left behind by an edit; drop both markers.
      edits.push({ start: a.idx, end: b.idx + 2, text: " " });
      i++;
    }
  }
  let out = line;
  for (const e of edits.sort((x, y) => y.start - x.start)) {
    out = out.slice(0, e.start) + e.text + out.slice(e.end);
  }
  return out;
}

export function repairEmphasis(md: string | null | undefined): string {
  if (!md) return "";
  return md.split("\n").map(repairEmphasisLine).join("\n");
}

function truncateAtBoundary(text: string, max: number): string {
  if (text.length <= max) return text;
  const cut = text.slice(0, max);
  const sentenceEnd = Math.max(
    cut.lastIndexOf(". "),
    cut.lastIndexOf("? "),
    cut.lastIndexOf("! "),
  );
  if (sentenceEnd >= 50) return cut.slice(0, sentenceEnd + 1);
  const space = cut.lastIndexOf(" ");
  return (
    (space > 0 ? cut.slice(0, space) : cut).replace(/[,;:\-–—\s]+$/, "") + "…"
  );
}

const STRUCTURAL_START = /^(?:#{1,6}\s|\||!\[|[-*+]\s|\d+[.)]\s|>|```|::|---)/;

const DISCLAIMER_WORDS =
  /\b(?:estimates?|guarantee|disclaimer|not financial advice|figures below|for informational)\b/i;

// A fallback excerpt must be a real intro sentence, not a table footnote
// "(Additional costs...)", an italic disclaimer, or a one-word FAQ answer.
function isProseCandidate(block: string): boolean {
  if (/^(?:[([]|[*_](?!\*))/.test(block)) return false;
  if (/^(?:yes|no)\b/i.test(block)) return false;
  if (DISCLAIMER_WORDS.test(block)) return false;
  return block.split(/\s+/).length >= 10;
}

// An excerpt must read as a sentence. A raw heading, table row or list line
// (what a table-first post used to store) is replaced by the first real
// prose paragraph of the article.
export function cleanExcerpt(
  raw: string | null | undefined,
  body?: string | null,
  max = 155,
): string {
  const rawText = stripModelChatter(raw);
  const structural = STRUCTURAL_START.test(rawText);
  const direct = toPlainText(rawText);
  if (!structural && direct.length >= 40)
    return truncateAtBoundary(direct, max);
  for (const block of (body ?? "").replace(/\r\n?/g, "\n").split(/\n\s*\n/)) {
    const b = block.trim();
    if (!b || STRUCTURAL_START.test(b) || !isProseCandidate(b)) continue;
    const text = toPlainText(b);
    if (text.length >= 60) return truncateAtBoundary(text, max);
  }
  return !structural && direct.length >= 20 ? direct : "";
}

// Site content is US-only. Foreign currency, UK/Canada/Australia property
// terms and spellings, metric units and foreign places mean the model drifted.
const NON_US_ANYCASE =
  /\b(?:stamp duty|council tax|conveyancing|freeholds?|leaseholds?|solicitors?|estate agents?|postcodes?|chartered surveyors?|buy[- ]to[- ]let|help to buy|pounds? sterling|euros?|rupees?|colour(?:s|ed|ful|ing)?|neighbour(?:s|hoods?)?|favourites?|behaviour|honour(?:s|ed)?|organis(?:e|ed|es|ing|ation)|realis(?:e|ed|es|ing)|recognis(?:e|ed|es|ing)|analys(?:e|ed|es|ing)|programmes?|licences?|cheques?|storeys?|mould|whilst|kerb|tyres?|kilomet(?:re|res)|metres?|litres?|centres?)\b/i;
const NON_US_EXACT =
  /[£€¥₹₩₽]|\b(?:HMRC|RRSP|TFSA|CMHC|UK|GBP|CAD|AUD|NZD|INR|EUR|Toronto|Vancouver|Mumbai|Delhi|Sydney|Canada|Canadian|Australia|Australian|United Kingdom|Britain|British|Scotland|Ireland|India)\b|(?<!New )\bEngland\b/;

// Reports up to five distinct passages so one rewrite pass can fix them all.
function nonUsContent(prose: string): StyleViolation[] {
  const found: StyleViolation[] = [];
  const seen = new Set<string>();
  for (const re of [NON_US_EXACT, NON_US_ANYCASE]) {
    const global = new RegExp(
      re.source,
      re.flags.includes("g") ? re.flags : `${re.flags}g`,
    );
    for (const m of prose.matchAll(global)) {
      if (m.index === undefined) continue;
      const excerpt = sentenceAround(prose, m.index, m[0].length);
      if (seen.has(excerpt)) continue;
      seen.add(excerpt);
      found.push({ rule: "non_us_content", excerpt });
      if (found.length >= 5) return found;
    }
  }
  return found;
}

// Commentary a model wraps around its answer ("Here is the rewritten ...:",
// "Note: I rewrote the passage ...") must never be stored as content. It is
// stripped deterministically and also reported as a violation so a rewrite
// pass can remove it at the source.
const LEADING_CHATTER =
  /^\s*(?:here(?:['’]s| is| are)\b[^\n:]{0,60}\b(?:rewritten|revised|updated|improved|corrected|edited)\b[^\n:]{0,100}:|here(?:['’]s| is)\b[^\n:]{0,40}\b(?:meta description|excerpt|article|takeaways)\s*:|below is\b[^\n:]{0,60}\b(?:rewritten|revised|updated|article)\b[^\n:]{0,80}:|sure[,!.]|certainly[,!.]|of course[,!.]|okay[,!.]|i(?:['’]ve| have) (?:rewritten|revised|updated)\b[^\n]{0,140}[:.])\s*/i;
const TRAILING_CHATTER_LINE =
  /^\s*(?:[-*_]{3,}\s*)?(?:\*\*)?(?:note|notes|changes made|what i changed|explanation)(?:\*\*)?\s*:|^\s*i\s+(?:rewrote|have rewritten|kept everything|removed the|changed the)\b|^\s*the (?:original|rewritten|revised) (?:sentence|passage|text|version)\b|^\s*(?:let me know|feel free to|i hope this)\b|^\s*[-*]\s+the original (?:sentence|passage) was\b/i;

export function stripModelChatter(input: string | null | undefined): string {
  let out = (input ?? "").trim();
  for (let i = 0; i < 3 && LEADING_CHATTER.test(out); i++) {
    out = out.replace(LEADING_CHATTER, "").trim();
  }
  const lines = out.split("\n");
  for (let i = Math.max(0, lines.length - 12); i < lines.length; i++) {
    if (TRAILING_CHATTER_LINE.test(lines[i])) {
      let cut = i;
      while (cut > 0 && /^\s*(?:[-*_]{3,})?\s*$/.test(lines[cut - 1])) cut--;
      out = lines.slice(0, cut).join("\n").trim();
      break;
    }
  }
  const quoted = out.match(/^(["“])([\s\S]*)(["”])$/);
  return quoted ? quoted[2].trim() : out;
}

function chatterViolation(text: string): StyleViolation | null {
  const trimmed = text.trim();
  const stripped = stripModelChatter(trimmed);
  if (stripped === trimmed) return null;
  const removed = trimmed.startsWith(stripped)
    ? trimmed.slice(stripped.length)
    : trimmed.slice(0, trimmed.length - stripped.length);
  return { rule: "model_chatter", excerpt: removed.trim().slice(0, 200) };
}

// A meta description must be a short plain sentence. Model commentary is
// removed; if what is left is not usable (empty, too short, or an article
// pasted in by mistake) it is derived from the article body instead.
export function cleanMetaDescription(
  raw: string | null | undefined,
  body?: string | null,
  max = 158,
): string {
  const text = toPlainText(stripModelChatter(raw));
  if (text.length >= 70 && text.length <= 300)
    return truncateAtBoundary(text, max);
  return cleanExcerpt("", body, max) || truncateAtBoundary(text, max);
}

export function findStyleViolations(
  content: string,
  rulesText: string | null | undefined,
): StyleViolation[] {
  const violations: StyleViolation[] = [];
  const text = normalize(content ?? "").slice(0, MAX_CONTENT_CHARS);
  if (!text.trim()) return violations;
  const prose = stripNonProse(text);

  // Brand name is always enforced: two words in prose, one word only inside
  // the literal domain.
  const brand = prose.match(/\bHomeApproach\b(?!\.com)/);
  if (brand && brand.index !== undefined) {
    violations.push({
      rule: "brand_name",
      excerpt: sentenceAround(prose, brand.index, brand[0].length),
    });
  }

  violations.push(...languageDrift(prose));
  violations.push(...nonUsContent(prose));

  const chatter = chatterViolation(text);
  if (chatter) violations.push(chatter);

  const parsed = parseStandingRules(rulesText);

  for (const phrase of parsed.bannedPhrases) {
    const re = new RegExp(
      `(?<![A-Za-z])${escapeRegExp(normalize(phrase))}(?![A-Za-z])`,
      "i",
    );
    const m = prose.match(re);
    if (m && m.index !== undefined) {
      violations.push({
        rule: `banned_phrase:${phrase}`,
        excerpt: sentenceAround(prose, m.index, m[0].length),
      });
    }
  }

  if (parsed.detectors.includes("contrast_negation")) {
    for (const paragraph of prose.split("\n")) {
      if (paragraph.length < 12) continue;
      for (const re of CONTRAST_PATTERNS) {
        const m = paragraph.match(re);
        if (m && m.index !== undefined) {
          violations.push({
            rule: "contrast_negation",
            excerpt: sentenceAround(paragraph, m.index, m[0].length),
          });
          break;
        }
      }
    }
  }

  if (parsed.detectors.includes("repetitive_headings")) {
    const v = repetitiveHeadings(text);
    if (v) violations.push(v);
  }

  return violations;
}

export function buildStandingRulesBlock(
  rulesText: string | null | undefined,
): string {
  const clean = (rulesText ?? "").trim().slice(0, MAX_RULES_CHARS);
  if (!clean) return "";
  return `\n\nSTANDING EDITORIAL RULES (LAW - set by the site owner, apply to every article, and win over any conflicting style instruction):\n${clean}\n\nBefore returning, re-read the article and rewrite any sentence that breaks a rule above.`;
}

export function buildViolationFeedback(violations: StyleViolation[]): string {
  const listed = violations
    .slice(0, MAX_FEEDBACK_ITEMS)
    .map((v) => `- [${v.rule}] "${v.excerpt}"`)
    .join("\n");
  const rules = new Set(violations.map((v) => v.rule));
  const extra: string[] = [];
  if (rules.has("language_drift")) {
    extra.push(
      "Write every sentence in plain American English. Translate any non-English passage; if the article is in another language, rewrite the whole article in English.",
    );
  }
  if (rules.has("non_us_content")) {
    extra.push(
      "This is a US-only site: use US spelling, US dollars ($), US terms (down payment, realtor, closing, title company, ZIP code) and US places only. Remove or replace every foreign currency, term, spelling and place.",
    );
  }
  if (rules.has("model_chatter")) {
    extra.push(
      "Return ONLY the article text. No introduction, no notes, no explanation of what you changed, no closing remarks.",
    );
  }
  if (rules.has("garbled_text")) {
    extra.push(
      "Replace every garbled or broken character (such as â€™ or Ã©) with normal punctuation and plain letters.",
    );
  }
  const tail = extra.length ? `\n\n${extra.join("\n")}` : "";
  return `Your draft broke the standing editorial rules in these passages:\n${listed}\n\nRewrite ONLY those passages so they comply, keep everything else exactly as it is, and return the full article.${tail}`;
}
